import {
  COL_GAP,
  GRID,
  NODE_MIN_HEIGHT,
  NODE_WIDTH,
  ROW_GAP,
  snapToGrid,
} from '../constants/csmlLayout';

const ORIGIN = GRID * 5;

/**
 * Lays the flow out left to right: the start node anchors column 0, every
 * node sits one column right of the first node that reaches it, and nodes in
 * a column are stacked in visiting order (parent first, then by branch port).
 * A node tries to sit level with its parent so straight chains stay straight.
 * Nodes nothing reaches go in a trailing column.
 *
 * Returns `{ [nodeId]: { x, y } }` and never mutates its input.
 */
export function computeAutoLayout(nodes, connections, heights = {}) {
  if (!nodes.length) return {};
  const heightOf = id => heights[id] || NODE_MIN_HEIGHT;

  const outgoing = {};
  connections.forEach(conn => {
    (outgoing[conn.fromId] ||= []).push(conn);
  });
  Object.values(outgoing).forEach(list =>
    list.sort((a, b) => (a.fromPortIdx ?? 0) - (b.fromPortIdx ?? 0))
  );

  const column = {};
  const parentOf = {};
  const order = [];
  const roots = nodes.filter(node => node.type === 'start');
  const queue = [...roots];
  roots.forEach(root => {
    column[root.id] = 0;
    order.push(root.id);
  });
  while (queue.length) {
    const current = queue.shift();
    (outgoing[current.id] || []).forEach(conn => {
      if (column[conn.toId] !== undefined) return;
      const target = nodes.find(node => node.id === conn.toId);
      if (!target) return;
      column[target.id] = column[current.id] + 1;
      parentOf[target.id] = current.id;
      order.push(target.id);
      queue.push(target);
    });
  }

  const orphanColumn = Math.max(0, ...Object.values(column)) + 1;
  nodes.forEach(node => {
    if (column[node.id] !== undefined) return;
    column[node.id] = orphanColumn;
    order.push(node.id);
  });

  const positions = {};
  const nextFreeY = {};
  order.forEach(id => {
    const col = column[id];
    const parentY = positions[parentOf[id]]?.y ?? ORIGIN;
    const y = Math.max(parentY, nextFreeY[col] ?? ORIGIN);
    positions[id] = {
      x: ORIGIN + col * (NODE_WIDTH + COL_GAP),
      y: snapToGrid(y),
    };
    nextFreeY[col] = positions[id].y + heightOf(id) + ROW_GAP;
  });
  return positions;
}
